import { HttpClient } from '@angular/common/http';
import { Component, computed, DestroyRef, HostListener, inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { environment } from 'src/environments/environment';
import { RecaptchaComponent, RecaptchaModule } from 'ng-recaptcha-2';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ModalService } from 'src/app/services/modal.service';
import { ModalNames } from 'src/app/common/modal.config';
import { ErrorMessageService } from 'src/app/services/error-message.util';
import { strictEmailValidator } from 'src/app/utils/validators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormDataStoreService } from 'src/app/services/form-data.store';

interface PasswordResetResult {
  success: boolean;
  data: {
    success: boolean;
    code: string | null;
  }
}

@Component({
  selector: 'app-forgotten-password',
  templateUrl:  `./${environment.pageFolder}/forgotten-password.${environment.pageFolder}.component.html`,
  styleUrls:  [`./${environment.pageFolder}/forgotten-password.${environment.pageFolder}.component.sass`],
  standalone: true,
  imports: [ReactiveFormsModule, RecaptchaModule, CommonModule, TranslateModule]
})

export class ForgottenPasswordComponent implements OnInit {
  successMessage: string = "";
  errorMessage: string = "";

  private readonly formDataKey = `${ModalNames.ForgottenPassword}`;
  forgottenPasswordForm: FormGroup;

  @ViewChild('captchaPassword', { static: false }) captchaPassword?: RecaptchaComponent;
  recaptchaSiteKey: string = environment.recaptchaSiteKey;
  private captchaResponse: string | null = null;

  public readonly modalNamesEnum = ModalNames;
  readonly isForgottenPassModalOpen = computed(() => this.modalService.modalsState().get(ModalNames.ForgottenPassword) ?? false);
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly http: HttpClient,
    private readonly modalService: ModalService,
    private readonly errorMessageService: ErrorMessageService,
    private readonly formDataStore: FormDataStoreService
  ) {
    this.forgottenPasswordForm = this.formBuilder.group({
      email: ['', [Validators.required, strictEmailValidator]],
    });
  }

  ngOnInit(): void {
    if(environment.configId === "4") {
      this.formDataStore.subscribeToForm(this.formDataKey, this.forgottenPasswordForm, this.destroyRef);
    }
  }

  resolved(captchaResponse: string | null) {
    this.captchaResponse = captchaResponse;

    if (this.errorMessage === this.errorMessageService.getErrorMessage('recaptchaUnchecked')) {
      this.errorMessage = "";
    }
  }

  onSubmit(): void {
    this.successMessage = '';

    if (this.forgottenPasswordForm.invalid) {
      this.forgottenPasswordForm.markAllAsTouched();
      this.errorMessage = this.errorMessageService.getErrorMessage('invalidEmail');
    } else if (!this.captchaResponse) {
      this.errorMessage = this.errorMessageService.getErrorMessage('recaptchaUnchecked');
    } else {
      const body = {
        email: this.forgottenPasswordForm.value.email,
        captchaToken: this.captchaResponse
      }

      this.http.post<PasswordResetResult>(
        `${environment.apiUrl}/v1/main/user/password/reset`, body).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: response => {
          if (response.success) {
            if (response.data.success) {
              this.successMessage = this.errorMessageService.getSuccessMessage('passwordChangedSuccess');
              this.errorMessage = '';
              this.forgottenPasswordForm.reset();

            } else if (!response.data.success && response.data.code === "ORA-20152") {
              this.errorMessage = this.errorMessageService.getErrorMessage('invalidEmail');

            } else {
              this.errorMessage = this.errorMessageService.getErrorMessage('requestError');
            }
          } else {
            this.errorMessage = this.errorMessageService.getErrorMessage('requestError');
          }

          this.formDataStore.clearFormData(this.formDataKey);
          this.resetCaptcha();
        },
        error: error => {
          this.errorMessage = this.errorMessageService.getErrorMessage('requestError');
          this.resetCaptcha();
        },
      });
    }
  }

  @HostListener('document:keydown.escape', ['$event']) onKeydownHandler(event: KeyboardEvent) {
    if (this.isForgottenPassModalOpen()) {
      this.modalService.closeModal(ModalNames.ForgottenPassword);
    }
  }

  closeForgottenPassword() {
    this.resetState();
    this.modalService.closeModal(this.modalNamesEnum.ForgottenPassword);
  }

  openLoginFromForgotten() {
    this.resetState();
    this.modalService.toggleModal(this.modalNamesEnum.ForgottenPassword, this.modalNamesEnum.Login);
  }

  private resetState() {
    this.errorMessage = '';
    this.successMessage = '';
    this.forgottenPasswordForm.reset();
    this.resetCaptcha();
  }

  private resetCaptcha(): void {
    this.captchaResponse = null;
    this.captchaPassword?.reset();
  }
}
